import { ConditionChild, ConditionGroup, PersonalizationFieldGroup } from '../types';

export const personalizationFieldKey = (element: ConditionChild) => `${element.type}:${element.id}`;

export type PersonalizationFieldSection =
  | { element: ConditionChild; group?: never }
  | { group: PersonalizationFieldGroup; elements: ConditionChild[] };

// Entries whose field no longer sits in the group's list (saved before linking
// a field to a condition kept groups in sync) are dropped, so they neither
// count towards the group nor pull the field back in later.
export function getPersonalizationFieldGroups(tree: ConditionGroup): PersonalizationFieldGroup[] {
  return Array.isArray(tree.fieldGroups)
    ? tree.fieldGroups
        .filter(
          (group) =>
            group &&
            typeof group.childrenKey === 'string' &&
            typeof group.id === 'string' &&
            typeof group.name === 'string' &&
            Array.isArray(group.elements),
        )
        .map((group) => ({
          ...group,
          elements: group.elements.filter(
            (element) =>
              element &&
              (element.type === 'condition'
                ? typeof element.id === 'string'
                : ['text', 'image', 'addon', 'line', 'rectangle'].includes(element.type) &&
                  typeof element.id === 'number'),
          ),
        }))
        .map((group) => {
          const available = tree.children[group.childrenKey];
          if (!available) {
            return group;
          }
          const present = new Set(available.map(personalizationFieldKey));
          return {
            ...group,
            elements: group.elements.filter((element) => present.has(personalizationFieldKey(element))),
          };
        })
        .filter((group) => group.elements.length > 0)
    : [];
}

export function groupPersonalizationFields(
  tree: ConditionGroup,
  elements: ConditionChild[],
  childrenKey: string,
): PersonalizationFieldSection[] {
  const owner = new Map<string, PersonalizationFieldGroup>();
  getPersonalizationFieldGroups(tree).forEach((group) => {
    if (group.childrenKey === childrenKey) {
      group.elements.forEach((element) => {
        if (element && !owner.has(personalizationFieldKey(element))) {
          owner.set(personalizationFieldKey(element), group);
        }
      });
    }
  });
  const result: PersonalizationFieldSection[] = [];
  const sections = new Map<PersonalizationFieldGroup, ConditionChild[]>();
  elements.forEach((element) => {
    const group = owner.get(personalizationFieldKey(element));
    if (!group) {
      result.push({ element });
      return;
    }
    let members = sections.get(group);
    if (!members) {
      members = [];
      sections.set(group, members);
      result.push({ group, elements: members });
    }
    members.push(element);
  });
  return result;
}

export function savePersonalizationFieldGroup(tree: ConditionGroup, group: PersonalizationFieldGroup) {
  const available = tree.children[group.childrenKey];
  if (!available || !group.id || !group.name.trim()) {
    return;
  }
  const selected = new Set(group.elements.map(personalizationFieldKey));
  const elements = available.filter((element) => selected.has(personalizationFieldKey(element)));
  const next = { ...group, name: group.name.trim(), elements };
  const groups = getPersonalizationFieldGroups(tree);
  const exists = groups.some((item) => item.id === group.id);
  const updated = groups.map((item) => {
    if (item.id === group.id) {
      return next;
    }
    return item.childrenKey === group.childrenKey
      ? { ...item, elements: item.elements.filter((element) => !selected.has(personalizationFieldKey(element))) }
      : item;
  });
  if (!exists) {
    updated.push(next);
  }
  setFieldGroups(tree, updated);
}

function setFieldGroups(tree: ConditionGroup, groups: PersonalizationFieldGroup[]) {
  tree.fieldGroups = groups.filter((item) => item.elements.length > 0);
  if (!tree.fieldGroups.length) {
    delete tree.fieldGroups;
  }
}

// Removes a field from the groups of the list it is leaving.
export function removeFromPersonalizationFieldGroups(
  tree: ConditionGroup,
  element: ConditionChild,
  childrenKey: string,
) {
  if (!tree.fieldGroups) {
    return;
  }
  const key = personalizationFieldKey(element);
  setFieldGroups(
    tree,
    tree.fieldGroups.map((group) =>
      group.childrenKey === childrenKey
        ? { ...group, elements: group.elements.filter((item) => personalizationFieldKey(item) !== key) }
        : group,
    ),
  );
}

// A field linked to a condition moves into the condition's option list, which a
// group of the old list cannot hold. The condition joins the group instead, so
// the field still shows inside it, nested under the option.
export function moveIntoConditionInPersonalizationFieldGroups(
  tree: ConditionGroup,
  element: ConditionChild,
  childrenKey: string,
  conditionId: string,
) {
  if (!tree.fieldGroups) {
    return;
  }
  const key = personalizationFieldKey(element);
  const condition: ConditionChild = { type: 'condition', id: conditionId };
  const conditionKey = personalizationFieldKey(condition);
  const conditionInList = tree.children[childrenKey]?.some((item) => personalizationFieldKey(item) === conditionKey);
  const conditionGrouped = tree.fieldGroups.some(
    (group) =>
      group.childrenKey === childrenKey &&
      group.elements.some((item) => personalizationFieldKey(item) === conditionKey),
  );
  setFieldGroups(
    tree,
    tree.fieldGroups.map((group) => {
      if (group.childrenKey !== childrenKey || !group.elements.some((item) => personalizationFieldKey(item) === key)) {
        return group;
      }
      const elements = group.elements.flatMap((item) => {
        if (personalizationFieldKey(item) !== key) {
          return [item];
        }
        return conditionInList && !conditionGrouped ? [condition] : [];
      });
      return { ...group, elements };
    }),
  );
}
